import { h, JSX } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { Modal } from './Modal';
import { TextField } from './TextField';
import { NumberInput } from './NumberInput';
import { TrashIcon } from './Icons';

export interface OpenGiftCardModalProps {
    isOpen: boolean;
    giftCardType: {
        id: number;
        name: string;
        min_amount?: number;
        max_amount?: number;
        unitPrice?: number;
        quantity?: number;
    } | null;
    onClose: () => void;
    onConfirm: (amount: number, quantity: number) => void;
    onDelete?: () => void;
    formatMoney: (amount: number) => string;
    translations: {
        title: string;
        amountLabel: string;
        quantityLabel: string;
        totalLabel: string;
        addToCart: string;
        save?: string;
        cancel: string;
        amountBetween: string;
    };
}

export const OpenGiftCardModal = (props: OpenGiftCardModalProps): JSX.Element | null => {
    const [amount, setAmount] = useState<string>('');
    const [quantity, setQuantity] = useState<number>(1);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (props.isOpen && props.giftCardType) {
            // If editing existing (has quantity > 0), populate with current values
            if (props.giftCardType.quantity && props.giftCardType.quantity > 0 && props.giftCardType.unitPrice > 0) {
                setAmount(props.giftCardType.unitPrice.toString());
                setQuantity(props.giftCardType.quantity);
            } else {
                // New gift card
                setAmount('');
                setQuantity(1);
            }
            setError('');
            // Focus the amount input when modal opens
            setTimeout(() => {
                const input = document.getElementById('open-gift-card-amount') as HTMLInputElement;
                if (input) {
                    input.focus();
                    // If editing, select all text after the prefix
                    if (props.giftCardType?.quantity && props.giftCardType.quantity > 0) {
                        input.setSelectionRange(2, input.value.length);
                    } else {
                        // Position cursor after the "€ " prefix for new entry
                        input.setSelectionRange(2, 2);
                    }
                }
            }, 100);
        }
    }, [props.isOpen, props.giftCardType]);

    if (!props.giftCardType) {
        return null;
    }

    const handleAmountChange = (value: string) => {
        // Remove currency symbol if present
        const cleanValue = value.replace('€', '').trim().replace(',', '.');
        
        // Allow only numbers and decimal point
        if (!/^\d*\.?\d*$/.test(cleanValue)) {
            return;
        }
        
        setAmount(cleanValue);
        setError('');
    };

    const handleConfirm = () => {
        const numericAmount = parseFloat(amount);
        const minAmount = props.giftCardType?.min_amount ?? 1;
        const maxAmount = props.giftCardType?.max_amount ?? 1000;
        
        if (isNaN(numericAmount) || numericAmount <= 0) {
            setError(props.translations.amountBetween
                .replace(':min', props.formatMoney(minAmount))
                .replace(':max', props.formatMoney(maxAmount)));
            // Select the amount field for easy re-entry
            setTimeout(() => {
                const input = document.getElementById('open-gift-card-amount') as HTMLInputElement;
                if (input) {
                    input.focus();
                    input.setSelectionRange(2, input.value.length); // Select all text after "€ "
                }
            }, 50);
            return;
        }

        if (numericAmount < minAmount || numericAmount > maxAmount) {
            setError(props.translations.amountBetween
                .replace(':min', props.formatMoney(minAmount))
                .replace(':max', props.formatMoney(maxAmount)));
            // Select the amount field for easy re-entry
            setTimeout(() => {
                const input = document.getElementById('open-gift-card-amount') as HTMLInputElement;
                if (input) {
                    input.focus();
                    input.setSelectionRange(2, input.value.length); // Select all text after "€ "
                }
            }, 50);
            return;
        }

        props.onConfirm(numericAmount, quantity);
        props.onClose();
    };

    const title = `${props.translations.title}: ${props.giftCardType.name}`;
    // Check if we're editing (has quantity > 0, which means it's from cart)
    const isEditing = !!(props.giftCardType.quantity && props.giftCardType.quantity > 0);
    const totalPrice = parseFloat(amount || '0') * quantity;

    return (
        <Modal
            show={props.isOpen}
            toggleModal={props.onClose}
            title={title}
            hasCloseButton
        >
            <div class='flex flex-col'>
                <div class='flex flex-col py-6 px-8 grow'>
                    <table class='[&>tr+tr>td]:pt-6'>
                        <tr>
                            <td class='font-semibold mr-6 align-top pt-3'>{props.translations.amountLabel}</td>
                            <td>
                                <TextField
                                    id="open-gift-card-amount"
                                    value={amount}
                                    onChange={handleAmountChange}
                                    placeholder={`${props.giftCardType.min_amount ?? 1} - ${props.giftCardType.max_amount ?? 1000}`}
                                    prefix="€"
                                    inputmode="decimal"
                                />
                            </td>
                        </tr>
                        {error && (
                            <tr>
                                <td></td>
                                <td class='text-red-500 text-sm pt-2'>{error}</td>
                            </tr>
                        )}
                        <tr>
                            <td class='font-semibold mr-6'>{props.translations.quantityLabel}</td>
                            <td>
                                <NumberInput
                                    value={quantity}
                                    onChange={setQuantity}
                                    min={1}
                                />
                            </td>
                        </tr>
                        {isEditing ? (
                            <tr>
                                <td class='font-semibold mr-6'>{props.translations.totalLabel}</td>
                                <td class='font-semibold'>{props.formatMoney(totalPrice)}</td>
                            </tr>
                        ) : null}
                    </table>
                </div>
                <div class='flex shrink justify-between items-center border-t px-8 py-6'>
                    {props.onDelete && (
                        <button 
                            onClick={props.onDelete}
                            class='btn btn-border p-3 text-kinola-red hover:text-kinola-red-h rounded-2xl'
                        >
                            <TrashIcon/>
                        </button>
                    )}
                    <div class='flex gap-2.5 ml-auto'>
                        <button 
                            onClick={props.onClose}
                            class='btn btn-border p-4 text-base leading-[14px]'
                        >
                            {props.translations.cancel}
                        </button>
                        <button 
                            onClick={handleConfirm}
                            disabled={!amount || parseFloat(amount) <= 0}
                            class={`btn p-4 text-base leading-[14px] ${
                                !amount || parseFloat(amount) <= 0 ? 'btn-disabled' : 'btn-light-blue'
                            }`}
                        >
                            {isEditing && props.translations.save ? props.translations.save : props.translations.addToCart}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
